// frontend/src/components/Leads/TeamAssignmentModal.js
import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import apiService from '../../services/apiService';
import LoadingSpinner from '../LoadingSpinner';
import { useUiLanguage } from '../../context/UiLanguageContext';

// Этот компонент будет использоваться для назначения адвоката и паралигала
// для конвертированного дела.
const TeamAssignmentModal = ({ caseId, caseName, onClose, onAssignmentSuccess }) => {
    const { t } = useUiLanguage();
    // Состояние для хранения списка доступных сотрудников
    const [staff, setStaff] = useState({ attorneys: [], paralegals: [] });
    // Состояние для хранения выбранного адвоката и паралигала
    const [selectedAttorney, setSelectedAttorney] = useState('');
    const [selectedParalegal, setSelectedParalegal] = useState('');
    // Состояние для отслеживания загрузки данных
    const [isLoading, setIsLoading] = useState(true);
    // Состояние для отслеживания процесса назначения
    const [isAssigning, setIsAssigning] = useState(false);

    // Хук для загрузки списка доступных сотрудников (адвокатов и паралигалов)
    useEffect(() => {
        const fetchStaff = async () => {
            try {
                // Предполагаем, что есть эндпоинты для получения сотрудников по роли
                const attorneys = await apiService.getUsersByRole('ATTORNEY');
                const paralegals = await apiService.getUsersByRole('PARALEGAL');
                
                setStaff({ attorneys, paralegals });
                
                // Предварительно выбираем первого доступного сотрудника, если есть
                if (attorneys.length > 0) {
                    setSelectedAttorney(attorneys[0].id);
                }
                if (paralegals.length > 0) {
                    setSelectedParalegal(paralegals[0].id);
                }

            } catch (error) {
                toast.error(t('practice.leads.team_modal_load_failed', { error: error.message }));
                onClose(); // Закрываем модальное окно при ошибке загрузки
            } finally {
                setIsLoading(false);
            }
        };
        fetchStaff();
    }, [onClose, t]);

    // Обработчик для отправки данных о назначении
    const handleSubmit = async () => {
        // Проверка, что выбраны оба сотрудника (или хотя бы один, если это не обязательно)
        if (!selectedAttorney && staff.attorneys.length > 0) {
            toast.error(t('practice.leads.team_modal_select_attorney_req'));
            return;
        }
        
        if (!selectedParalegal && staff.paralegals.length > 0) {
            toast.error(t('practice.leads.team_modal_select_paralegal_req'));
            return;
        }
        
        // Формируем данные для API
        const assignmentData = {
            attorney_id: selectedAttorney || null,
            paralegal_id: selectedParalegal || null,
        };

        try {
            setIsAssigning(true);
            // Вызываем новый метод API для назначения команды
            await apiService.assignTeamToCase(caseId, assignmentData);
            
            toast.success(t('practice.leads.team_modal_success', { caseName }));
            onAssignmentSuccess(); // Вызываем колбэк для обновления состояния на LeadsPage
            onClose(); // Закрываем модальное окно

        } catch (error) {
            toast.error(t('practice.leads.team_modal_failed', { error: error.message }));
        } finally {
            setIsAssigning(false);
        }
    };

    if (isLoading) {
        return (
            <div className="modal-backdrop">
                <div className="modal-content">
                    <LoadingSpinner message={t('practice.leads.team_modal_loading_staff')} />
                </div>
            </div>
        );
    }

    // Проверяем, есть ли вообще сотрудники для назначения
    const hasStaff = staff.attorneys.length > 0 || staff.paralegals.length > 0;

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{t('practice.leads.team_modal_title', { caseName })}</h2>
                    <button onClick={onClose} className="modal-close-btn">&times;</button>
                </div>
                <div className="modal-body">
                    {!hasStaff && (
                        <p className="text-danger">{t('practice.leads.team_modal_no_staff')}</p>
                    )}
                    {hasStaff && (
                        <form>
                            <div className="form-group">
                                <label htmlFor="attorney-select">{t('practice.leads.team_modal_attorney')}</label>
                                <select
                                    id="attorney-select"
                                    className="form-control"
                                    value={selectedAttorney}
                                    onChange={(e) => setSelectedAttorney(e.target.value)}
                                    disabled={staff.attorneys.length === 0 || isAssigning}
                                >
                                    {staff.attorneys.length === 0 ? (
                                        <option value="">{t('practice.leads.team_modal_no_attorneys')}</option>
                                    ) : (
                                        <>
                                            <option value="">{t('practice.leads.team_modal_select_attorney')}</option>
                                            {staff.attorneys.map(user => (
                                                <option key={user.id} value={user.id}>
                                                    {user.full_name} ({user.email})
                                                </option>
                                            ))}
                                        </>
                                    )}
                                </select>
                            </div>
                            
                            <div className="form-group mt-3">
                                <label htmlFor="paralegal-select">{t('practice.leads.team_modal_paralegal')}</label>
                                <select
                                    id="paralegal-select"
                                    className="form-control"
                                    value={selectedParalegal}
                                    onChange={(e) => setSelectedParalegal(e.target.value)}
                                    disabled={staff.paralegals.length === 0 || isAssigning}
                                >
                                    {staff.paralegals.length === 0 ? (
                                        <option value="">{t('practice.leads.team_modal_no_paralegals')}</option>
                                    ) : (
                                        <>
                                            <option value="">{t('practice.leads.team_modal_select_paralegal')}</option>
                                            {staff.paralegals.map(user => (
                                                <option key={user.id} value={user.id}>
                                                    {user.full_name} ({user.email})
                                                </option>
                                            ))}
                                        </>
                                    )}
                                </select>
                            </div>
                        </form>
                    )}
                </div>
                <div className="modal-footer">
                    <button 
                        onClick={onClose} 
                        className="btn btn-secondary"
                        disabled={isAssigning}
                    >
                        {t('practice.leads.team_modal_btn_cancel')}
                    </button>
                    <button 
                        onClick={handleSubmit} 
                        className="btn btn-primary"
                        disabled={isAssigning || !hasStaff}
                    >
                        {isAssigning ? t('practice.leads.team_modal_assigning') : t('practice.leads.team_modal_btn_assign')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TeamAssignmentModal;